// Libraries:
import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import PropTypes from "prop-types";
import format from "date-fns/format";

// Components:
import PageMeta from "core/PageMeta";
import MoreFromThis from "core/MoreFromThis";
import AboutThisImage from "core/AboutThisImage";
import PhotoDetailSteps from "photos/PhotoDetailSteps";
// import PhotoVisuallySimilar from "./PhotoVisuallySimilar";

// Paths:
import {
    BROWSE_BY_SHOOT_ID,
    PHOTO_DETAIL,
    BROWSE_BY_TAG,
    BROWSE_BY_CAMPAIGN
} from "data/links";

// Helpers
import { removeTwoDots } from "js/helpers/functions";
import { MORE_PHOTOS_FROM_SHOOT_BY_PHOTO_ID } from "photos/PhotoSearchResults/queries";
import { getMoreFromThisData } from "core/MoreFromThis/helpers";
import { getTagsData } from "core/TagToggle/helpers";
import { getCampaignsData } from "core/AboutThisImage/helpers";

const PhotoDetail = ({ photo, photoId }) => {
    const [moreFromThisShootResponse, setMoreFromThisShootResponse] = useState(
        null
    );
    const [photoItems, setPhotoItems] = useState([]);
    const [photoDetail, setPhotoDetail] = useState(null);
    const [photoTags, setPhotoTags] = useState([]);
    const [campaignItems, setCampaignItems] = useState([]);

    const setData = () => {
        setMoreFromThisShootResponse(data.morePhotosFromThisShoot);
        setPhotoDetail(data.photoDetail);
    };

    const [loadMoreFromShoot, { called, loading, data }] = useLazyQuery(
        MORE_PHOTOS_FROM_SHOOT_BY_PHOTO_ID,
        {
            fetchPolicy: "cache-and-network",
            onCompleted: setData
        }
    );

    /****************************
     *
     *
     * More From This Shoot:
     *
     * **************************/

    if (photo && photo.id && !called) {
        loadMoreFromShoot({
            variables: {
                photoId: photo.id,
                limit: 50
            }
        });
    }

    const moreFromThisShootHeading = "More From This Shoot";

    // // create view all link for "More From This Shoot":
    const firstPhoto =
        moreFromThisShootResponse &&
        moreFromThisShootResponse.items[0] &&
        moreFromThisShootResponse.items[0].photoShoot;
    const shootSlugWithId = (firstPhoto && firstPhoto.slugWithId) || "";
    const moreFromThisShootViewAllLink = BROWSE_BY_SHOOT_ID.replace(
        ":shootInfo",
        `${shootSlugWithId}`
    );

    // create each "More From This Shoot" image data-testid:
    const moreFromThisShootDataTestId = "more-from-shoot-photo";

    /** */

    /****************************
     *
     *
     * Upload date & Tags:
     *
     * **************************/
    const hasCreatedAt = photo && photo.createdAt;

    let formattedCreatedAt = "";

    if (hasCreatedAt) {
        formattedCreatedAt = format(new Date(photo.createdAt), "MMMM DD, YYYY");
    }

    const hasPhotoTags = photo && photo.tags;

    let imageTags = [];

    if (hasPhotoTags) {
        imageTags = photo.tags; // setting photoTags to photo.tags was causing an infinite re-render loop
    }
    /** */

    // create reusable const that tells "More from this shoot", "tags", and "campaigns" helpers what type of detail page this is:
    const pageContentType = "photos";

    useEffect(() => {
        // reformat data for More From This Shoot for component:
        if (moreFromThisShootResponse) {
            setPhotoItems(
                getMoreFromThisData({
                    items: moreFromThisShootResponse.items,
                    detailUrlPath: PHOTO_DETAIL,
                    detailUrlPathReplace: ":photoSlugWithId",
                    type: pageContentType
                })
            );
        }

        // reformat data for Tags for component:
        if (hasPhotoTags) {
            setPhotoTags(
                getTagsData({
                    tags: imageTags,
                    tagPath: BROWSE_BY_TAG,
                    tagPathReplace: ":tag",
                    type: pageContentType
                })
            );
        }

        // reformat data for Campaigns for component:
        if (photoDetail) {
            setCampaignItems(
                getCampaignsData({
                    campaigns: photoDetail.campaigns,
                    campaignPath: BROWSE_BY_CAMPAIGN,
                    campaignPathReplace: ":campaignDetail",
                    type: pageContentType
                })
            );
        }
    }, [moreFromThisShootResponse, photoDetail]);

    return (
        <>
            {photo && (
                <PageMeta
                    title={`${photo.title} | Noun Project`}
                    removeDescriptionTag
                >
                    <meta
                        data-testid="photo-detail-meta"
                        name="description"
                        content={removeTwoDots(
                            `${photo.description}. Download this picture and other Noun Project photos today!`
                        )}
                    />
                </PageMeta>
            )}

            <PhotoDetailSteps photoId={photoId} photo={photo} />

            <MoreFromThis
                items={photoItems}
                heading={moreFromThisShootHeading}
                viewAllLink={moreFromThisShootViewAllLink}
                eachDataTestId={moreFromThisShootDataTestId}
            />

            <AboutThisImage
                sectionHeading="About This Photo"
                sectionDataTestId="photo-detail-about"
                image={photo}
                tags={photoTags}
                campaignLinkPrefix="This photo is included in:"
                campaigns={campaignItems}
                createdAt={formattedCreatedAt}
            />
            {/* {response.items.length >= 3 &&
                <PhotoVisuallySimilar results={response} photoId={photoId}/>
            } */}
        </>
    );
};

PhotoDetail.propTypes = {
    photoId: PropTypes.string.isRequired,
    photo: PropTypes.object
};

export default PhotoDetail;
